'use client';

import { useI18n } from '@/contexts/I18nContext';

import { ArrowUp, Paperclip } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { detectMentionQuery, stripMentionFragment } from '@/lib/editor/agent/mentions';
import { CANNY_ACCEPT } from './useCannyDocuments';
import { CannyMentionMenu, type MentionItem } from './CannyMentionMenu';

interface CannyComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  busy: boolean;
  autoFocus?: boolean;
  onAttach?: (file: File) => void;
  mentionItems?: MentionItem[];
  onMention?: (item: MentionItem) => void;
}

export function CannyComposer({
  value,
  onChange,
  onSubmit,
  busy,
  autoFocus,
  onAttach,
  mentionItems,
  onMention,
}: CannyComposerProps) {
  const { t } = useI18n();
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [mention, setMention] = useState<{ query: string; start: number; end: number } | null>(null);

  // Высота по содержимому: композер — главный элемент экрана, скролл внутри
  // однострочного поля выглядит сломанным.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 200)}px`;
  }, [value]);

  const canSend = value.trim().length > 0 && !busy;
  const mentionOpen = Boolean(mention && mentionItems && onMention);

  return (
    <form
      className="liquid-glass relative w-full max-w-2xl rounded-3xl p-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSend) onSubmit();
      }}
    >
      {mentionOpen && mention && (
        <CannyMentionMenu
          query={mention.query}
          items={mentionItems ?? []}
          onPick={(item) => {
            // Сам меншен из текста убираем — он становится вложением запроса,
            // а не остаётся строкой «@брендбук.pdf» в сообщении.
            onChange(stripMentionFragment(value, mention));
            onMention?.(item);
            setMention(null);
            ref.current?.focus();
          }}
          onClose={() => setMention(null)}
        />
      )}
      <div className="flex items-end gap-2">
        {onAttach && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={`${CANNY_ACCEPT},image/*`}
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onAttach(file);
                // Сбрасываем значение: иначе повторный выбор того же файла
                // не вызовет change и вложение молча не добавится.
                event.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label={t('canny.composer.attach')}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white/45 transition-colors hover:text-white/80"
            >
              <Paperclip className="h-4 w-4" />
            </button>
          </>
        )}
        <textarea
          ref={ref}
          rows={1}
          autoFocus={autoFocus}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            if (!mentionItems || !onMention) return;
            setMention(detectMentionQuery(event.target.value, event.target.selectionStart ?? 0));
          }}
          onKeyDown={(event) => {
            // Пока открыто меню меншенов, Enter выбирает пункт, а не отправляет
            // сообщение — обработка живёт в самом меню.
            if (mentionOpen && ['Enter', 'Tab', 'ArrowUp', 'ArrowDown', 'Escape'].includes(event.key)) {
              return;
            }
            // Enter отправляет, Shift+Enter переносит строку — как везде.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (canSend) onSubmit();
            }
          }}
          placeholder={t('canny.composer.placeholder')}
          className="max-h-52 flex-1 resize-none bg-transparent px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none"
        />
        <button
          type="submit"
          disabled={!canSend}
          aria-label={t('canny.composer.send')}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/90 text-ink-950 transition-opacity disabled:opacity-30"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
